// CheckNoteDetailModal.tsx - Çek/Senet Detay Görüntüleme Modalı
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tab,
  Tabs
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import HistoryIcon from '@mui/icons-material/History';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LinkIcon from '@mui/icons-material/Link';
import {
  CheckNoteDto,
  CheckNoteType,
  CheckNoteDirection,
  CheckNoteStatus,
  CheckNoteTransactionDto,
  getStatusColor,
  formatCurrency,
  getCheckNoteTransactions,
  getCheckNotes
} from '../../../services/checkNoteService';

interface CheckNoteDetailModalProps {
  open: boolean;
  onClose: () => void;
  checkNote: CheckNoteDto | null;
  onEdit: () => void;
  onStatusChange: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const CheckNoteDetailModal: React.FC<CheckNoteDetailModalProps> = ({
  open,
  onClose,
  checkNote,
  onEdit,
  onStatusChange
}) => {
  const { t, i18n } = useTranslation('common2');
  const localeCode = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const [tabValue, setTabValue] = useState(0);
  const [transactions, setTransactions] = useState<CheckNoteTransactionDto[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [relatedNotes, setRelatedNotes] = useState<CheckNoteDto[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  
  // BATCH kodunu notes'dan çıkar
  const extractBatchCode = (notes: string | undefined): string | null => {
    if (!notes) return null;
    const match = notes.match(/\[BATCH-([A-Z0-9]+)\]/);
    return match ? match[0] : null;
  };
  
  const batchCode = checkNote ? extractBatchCode(checkNote.notes) : null;
  
  // Bağlı senetleri yükle
  useEffect(() => {
    const loadRelatedNotes = async () => {
      if (!checkNote || !open || !batchCode) return;
      
      setLoadingRelated(true);
      try {
        const response = await getCheckNotes({
          pageSize: 100
        });
        // Aynı batch koduna sahip senetleri filtrele
        const related = response.items.filter(note => 
          note.id !== checkNote.id && 
          note.notes?.includes(batchCode)
        );
        setRelatedNotes(related);
      } catch (err) {
        console.error('Bağlı senetler yüklenemedi:', err);
        setRelatedNotes([]);
      } finally {
        setLoadingRelated(false);
      }
    };
    
    if (tabValue === 2) {
      loadRelatedNotes();
    }
  }, [checkNote, open, tabValue, batchCode]);
  
  // İşlem geçmişini yükle
  useEffect(() => {
    const loadTransactions = async () => {
      if (!checkNote || !open) return;
      
      setLoadingTransactions(true);
      try {
        const data = await getCheckNoteTransactions(checkNote.id);
        setTransactions(data);
      } catch (err) {
        console.error('İşlem geçmişi yüklenemedi:', err);
        setTransactions([]);
      } finally {
        setLoadingTransactions(false);
      }
    };
    
    if (tabValue === 1) {
      loadTransactions();
    }
  }, [checkNote, open, tabValue]);
  
  if (!checkNote) return null;
  
  const isCheck = checkNote.type === CheckNoteType.CHECK;

  const decodeMojibake = (value?: string | null): string => {
    if (!value) return '';
    let decoded = value;
    for (let i = 0; i < 2; i += 1) {
      if (!/[ÃÂÅÄ]/.test(decoded)) break;
      try {
        const next = decodeURIComponent(escape(decoded));
        if (!next || next === decoded) break;
        decoded = next;
      } catch {
        break;
      }
    }
    return decoded;
  };

  const getLocalizedTypeLabel = (type: string): string => {
    if (type === CheckNoteType.CHECK) return t('checksNotes.types.check');
    if (type === CheckNoteType.PROMISSORY_NOTE) return t('checksNotes.types.promissoryNote');
    return type;
  };

  const getLocalizedDirectionLabel = (direction: string): string => {
    if (direction === CheckNoteDirection.RECEIVED) return t('checksNotes.directions.received');
    if (direction === CheckNoteDirection.GIVEN) return t('checksNotes.directions.given');
    return direction;
  };

  const getLocalizedStatusLabel = (status: string): string => {
    const keyMap: Record<string, string> = {
      PORTFOLIO: 'portfolio',
      IN_COLLECTION: 'inCollection',
      COLLECTED: 'collected',
      PARTIAL_COLLECTED: 'partialCollected',
      ENDORSED: 'endorsed',
      COLLATERAL: 'collateral',
      COLLATERAL_RETURNED: 'collateralReturned',
      BOUNCED: 'bounced',
      RETURNED: 'returned',
      CANCELED: 'canceled',
      GIVEN_PENDING: 'givenPending',
      GIVEN_PAID: 'givenPaid',
      GIVEN_RETURNED: 'givenReturned',
    };
    const key = keyMap[status];
    return key ? t(`checksNotes.statuses.${key}`) : status;
  };
  
  // Tarihi formatla
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString(localeCode, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  // İşlem türünü çevir
  const getTransactionTypeLabel = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      'CREATED': t('checksNotes.transactionTypes.created'),
      'STATUS_CHANGED': t('checksNotes.transactionTypes.statusChange'),
      'STATUS_CHANGE': t('checksNotes.transactionTypes.statusChange'),
      'SENT_TO_COLLECTION': t('checksNotes.transactionTypes.sentToCollection'),
      'COLLECTED': t('checksNotes.transactionTypes.collected'),
      'PARTIAL_COLLECTED': t('checksNotes.transactionTypes.partialCollected'),
      'ENDORSED': t('checksNotes.transactionTypes.endorsed'),
      'COLLATERAL_GIVEN': t('checksNotes.transactionTypes.collateralGiven'),
      'COLLATERAL_RETURNED': t('checksNotes.transactionTypes.collateralReturned'),
      'BOUNCED': t('checksNotes.transactionTypes.bounced'),
      'RETURNED': t('checksNotes.transactionTypes.returned'),
      'CANCELED': t('checksNotes.transactionTypes.canceled'),
      'CANCELLED': t('checksNotes.transactionTypes.canceled'),
      'AMOUNT_UPDATED': t('checksNotes.transactionTypes.amountUpdated'),
      'DUE_DATE_UPDATED': t('checksNotes.transactionTypes.dueDateUpdated'),
      'PAYMENT': t('checksNotes.transactionTypes.payment'),
      'ENDORSEMENT': t('checksNotes.transactionTypes.endorsement'),
      'COLLATERAL': t('checksNotes.transactionTypes.collateral'),
      'UPDATED': t('checksNotes.transactionTypes.updated'),
      'GIVEN_PENDING': t('checksNotes.statuses.givenPending'),
      'GIVEN_PAID': t('checksNotes.statuses.givenPaid'),
      'GIVEN_RETURNED': t('checksNotes.statuses.givenReturned')
    };
    return typeMap[type] || type;
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: isCheck ? 'primary.main' : 'secondary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isCheck ? <CheckIcon /> : <DescriptionIcon />}
          <Typography variant="h6">
            {checkNote.documentNumber || (isCheck ? t('checksNotes.types.check') : t('checksNotes.types.promissoryNote'))}
          </Typography>
          {checkNote.isOverdue && (
            <Chip 
              icon={<WarningAmberIcon />}
              label={t('checksNotes.overdue')} 
              size="small" 
              color="warning"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label={t('checksNotes.tabs.details')} />
            <Tab label={t('checksNotes.tabs.history')} icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="end" />
            {batchCode && (
              <Tab label={t('checksNotes.tabs.relatedNotes')} icon={<LinkIcon sx={{ fontSize: 18 }} />} iconPosition="end" />
            )}
          </Tabs>
        </Box>
        
        {/* Detaylar Sekmesi */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Genel Bilgiler */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('checksNotes.detail.generalInfo')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">{t('checksNotes.table.type')}</Typography>
                    <Box>
                      <Chip 
                        label={getLocalizedTypeLabel(checkNote.type)} 
                        size="small"
                        color={isCheck ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">{t('checksNotes.table.direction')}</Typography>
                    <Box>
                      <Chip 
                        label={getLocalizedDirectionLabel(checkNote.direction)} 
                        size="small"
                        color={checkNote.direction === CheckNoteDirection.RECEIVED ? 'success' : 'error'}
                        variant="filled"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">{t('checksNotes.table.status')}</Typography>
                    <Box>
                      <Chip 
                        label={getLocalizedStatusLabel(checkNote.status)} 
                        size="small"
                        color={getStatusColor(checkNote.status) as 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | 'default'}
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">{t('checksNotes.detail.daysUntilDue')}</Typography>
                    <Typography variant="body1" fontWeight="bold" color={checkNote.isOverdue ? 'error' : 'text.primary'}>
                      {checkNote.daysUntilDue} {t('checksNotes.detail.days')}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Cari Hesap Bilgileri */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('checksNotes.detail.accountInfo')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body1" fontWeight="bold">{decodeMojibake(checkNote.accountName) || '-'}</Typography>
              </Paper>
            </Grid>
            
            {/* Tarihler */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('checksNotes.detail.dates')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">{t('checksNotes.form.issueDate')}</Typography>
                    <Typography variant="body2">{formatDate(checkNote.issueDate)}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">{t('checksNotes.form.issuePlace')}</Typography>
                    <Typography variant="body2">{checkNote.issuePlace || '-'}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">{t('checksNotes.form.dueDate')}</Typography>
                    <Typography variant="body2" fontWeight="bold" color={checkNote.isOverdue ? 'error' : 'inherit'}>
                      {formatDate(checkNote.dueDate)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Çek/Senet Bilgileri */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {isCheck ? t('checksNotes.detail.checkInfo') : t('checksNotes.detail.promissoryNoteInfo')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                {isCheck ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">{t('checksNotes.form.bankName')}</Typography>
                      <Typography variant="body2">{checkNote.bankName || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">{t('checksNotes.form.bankBranchName')}</Typography>
                      <Typography variant="body2">{checkNote.bankBranchName || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">{t('checksNotes.form.checkNumber')}</Typography>
                      <Typography variant="body2">{checkNote.checkNumber || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">{t('checksNotes.form.checkAccountNumber')}</Typography>
                      <Typography variant="body2">{checkNote.checkAccountNumber || '-'}</Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t('checksNotes.form.drawerName')}</Typography>
                      <Typography variant="body2">{checkNote.drawerName || '-'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t('checksNotes.form.guarantorName')}</Typography>
                      <Typography variant="body2">{checkNote.guarantorName || '-'}</Typography>
                    </Grid>
                  </Grid>
                )}
              </Paper>
            </Grid>
            
            {/* Tutar Bilgileri */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('checksNotes.detail.amountInfo')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.50' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">{t('checksNotes.table.amount')}</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {formatCurrency(checkNote.amount, checkNote.currency)}
                    </Typography>
                  </Grid>
                  {checkNote.currency !== 'TRY' && (
                    <>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">{t('checksNotes.form.exchangeRate')}</Typography>
                        <Typography variant="body2">{checkNote.exchangeRate}</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary">{t('checksNotes.detail.amountTRY')}</Typography>
                        <Typography variant="body2">{formatCurrency(checkNote.amountTRY, 'TRY')}</Typography>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">{t('checksNotes.detail.collectedAmount')}</Typography>
                    <Typography variant="body2" color="success.main">{formatCurrency(checkNote.collectedAmount, checkNote.currency)}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="caption" color="text.secondary">{t('checksNotes.detail.remainingAmount')}</Typography>
                    <Typography variant="body2" color="error.main">{formatCurrency(checkNote.remainingAmount, checkNote.currency)}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Ciro Bilgileri (Ciro Edildi durumunda veya ciro bilgisi varsa) */}
            {(checkNote.status === CheckNoteStatus.ENDORSED || checkNote.endorsedToAccountName || checkNote.endorsedToAccountId) && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('checksNotes.detail.endorsementInfo')}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.50' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t('checksNotes.detail.endorsedTo')}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {checkNote.endorsedToAccountName || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t('checksNotes.detail.endorsedDate')}</Typography>
                      <Typography variant="body2">{formatDate(checkNote.endorsedDate)}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}
            

            {/* Notlar */}
            {checkNote.notes && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('checksNotes.form.notes')}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2">{checkNote.notes}</Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </TabPanel>
        
        {/* İşlem Geçmişi Sekmesi */}
        <TabPanel value={tabValue} index={1}>
          {loadingTransactions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : transactions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">{t('checksNotes.detail.noTransactions')}</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('checksNotes.detail.transactionDate')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('checksNotes.detail.transactionType')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('checksNotes.detail.fromStatus')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('checksNotes.detail.toStatus')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('checksNotes.detail.amount')}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{t('checksNotes.detail.description')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((tx) => {
                    // Açıklama: Ciro işleminde ciro edilen kişiyi göster
                    let displayDescription = tx.description || '';
                    if (tx.transactionType === 'ENDORSED' && tx.relatedAccountName) {
                      displayDescription = `${t('checksNotes.detail.endorsePrefix')}: ${tx.relatedAccountName}`;
                    } else if (tx.relatedAccountName && !displayDescription) {
                      displayDescription = tx.relatedAccountName;
                    }
                    
                    return (
                      <TableRow key={tx.id}>
                        <TableCell>{formatDate(tx.transactionDate)}</TableCell>
                        <TableCell>{getTransactionTypeLabel(tx.transactionType)}</TableCell>
                        <TableCell>
                          {tx.fromStatus ? (
                            <Chip 
                              label={getLocalizedStatusLabel(tx.fromStatus as any)} 
                              size="small" 
                              variant="outlined"
                              sx={{ bgcolor: getStatusColor(tx.fromStatus as any) + '20' }}
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {tx.toStatus ? (
                            <Chip 
                              label={getLocalizedStatusLabel(tx.toStatus as any)} 
                              size="small" 
                              variant="outlined"
                              sx={{ bgcolor: getStatusColor(tx.toStatus as any) + '20' }}
                            />
                          ) : '-'}
                        </TableCell>
                        <TableCell>{tx.amount ? formatCurrency(tx.amount, 'TRY') : '-'}</TableCell>
                        <TableCell>{displayDescription || '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
        
        {/* Bağlı Senetler Sekmesi */}
        {batchCode && (
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('checksNotes.detail.seriesCode')}: <Chip label={batchCode} size="small" variant="outlined" sx={{ ml: 1 }} />
              </Typography>
            </Box>
            
            {loadingRelated ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : relatedNotes.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                {t('checksNotes.detail.noRelatedNotes')}
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>{t('checksNotes.table.documentNumber')}</TableCell>
                      <TableCell>{t('checksNotes.table.dueDate')}</TableCell>
                      <TableCell align="right">{t('checksNotes.table.amount')}</TableCell>
                      <TableCell>{t('checksNotes.table.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {relatedNotes.map((note, idx) => (
                      <TableRow 
                        key={note.id}
                        sx={{ 
                          bgcolor: note.isOverdue 
                            ? 'rgba(255, 152, 0, 0.12)' // Vadesi geçmiş - turuncu
                            : note.daysUntilDue <= 0 
                              ? 'rgba(76, 175, 80, 0.08)' // Bugün vadeli - yeşil
                              : 'inherit'
                        }}
                      >
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{note.documentNumber}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {formatDate(note.dueDate)}
                            {note.isOverdue && (
                              <WarningAmberIcon color="warning" sx={{ fontSize: 16 }} />
                            )}
                            {note.daysUntilDue <= 0 && !note.isOverdue && (
                              <Chip label={t('checksNotes.detail.today')} size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatCurrency(note.amount, note.currency)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={getLocalizedStatusLabel(note.status)} 
                            size="small"
                            color={getStatusColor(note.status) as any}
                            variant="outlined"
                            sx={{ height: 22 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>
          {t('checksNotes.form.close')}
        </Button>
        <Button 
          variant="outlined" 
          startIcon={<SwapHorizIcon />}
          onClick={() => {
            onClose();
            onStatusChange();
          }}
        >
          {t('checksNotes.actions.changeStatus')}
        </Button>
        <Button 
          variant="contained" 
          color={isCheck ? 'primary' : 'secondary'}
          startIcon={<EditIcon />}
          onClick={() => {
            onClose();
            onEdit();
          }}
        >
          {t('checksNotes.actions.edit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckNoteDetailModal;
